import Button from 'components/button-new';
import ProductCard from 'components/product-card';
import ProductCardSkeleton from 'components/product-card/skeleton/index.';
import ProductImpressionsTracker from 'components/product-impressions-tracker';
import { UrlQuery } from 'constants/test/dynamic-ux-test/url-query';
import { usePetProfile } from 'contexts/pet-profile';
import { m } from 'framer-motion';
import isPetProfileEmpty from 'modules/pet-profile/personalized-cdp/utils/is-pet-profile-empty';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { gtm, selectElement } from 'tracking';
import { State } from '../../state/interface';
import NoResultInfo from '../no-result-info';
import useNeedCaptureScenarios from './state';

interface Props {
  petType: 'cat' | 'dog';
  state: State;
}

/**
 * A component that shows the list of products that match the selection.
 */
const ProductsList: React.FC<Props> = ({ petType, state }) => {
  const { isProfileMinimal } = usePetProfile();
  const [listHasBeenRendered, setListHasBeenRendered] = useState(false);
  const { push } = useRouter();
  const { ctaLink, products, isLoading, title } = useNeedCaptureScenarios(
    petType,
    state
  );

  const isStateEmpty = isPetProfileEmpty({ petProfile: state });

  const variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { delay: 0.5 } },
  };

  const additionalQuery = isProfileMinimal
    ? undefined
    : { [UrlQuery.multiSegmentMode]: '1' };

  useEffect(() => {
    if (!listHasBeenRendered) {
      setListHasBeenRendered(true);
    }
  }, [listHasBeenRendered, products.length]);

  if (!listHasBeenRendered || isStateEmpty) return null;

  return (
    <div className="pt-6 lg:pt-0">
      <m.div
        className="bg-dog-base -mx-4 px-4 lg:pb-10"
        initial="hidden"
        animate="visible"
        exit="hidden"
        variants={variants}
      >
        <p className="text-primary-main font-black lg:container lg:mx-auto">
          {title ? (
            title
          ) : (
            <FormattedMessage id="landingpage:need-capture:products-list:title" />
          )}
        </p>
        {!isStateEmpty && !isLoading && products.length === 0 ? (
          <div className="mt-3 lg:container lg:mx-auto">
            <NoResultInfo />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 py-4 lg:container lg:mx-auto lg:grid-cols-3">
            {isLoading &&
              products.length === 0 &&
              new Array(3)
                .fill(null)
                .map((_, index) => <ProductCardSkeleton key={index} />)}

            {products.map((product, index) => (
              <ProductImpressionsTracker
                element="Need Capture"
                key={`${product.id}`}
                list="Need Capture"
                position={index}
                product={product}
                trackingGroupName="need-capture"
              >
                <div data-test="need-capture-product-tile">
                  <ProductCard
                    isBestSeller={product.getIsBestSeller()}
                    key={product.id}
                    position={index}
                    product={product}
                    collectionTitle={'Need Capture'}
                    trackingElementName={['Need Capture', 'Product Tile']}
                    additionalQuery={additionalQuery}
                  />
                </div>
              </ProductImpressionsTracker>
            ))}
          </div>
        )}

        <Button
          size="large"
          className="m-auto flex! lg:w-auto!"
          onClick={() => {
            ctaLink && push(ctaLink);
            gtm(
              selectElement({
                element: ['Need Capture', 'View All Products'],
              })
            );
          }}
        >
          <FormattedMessage id="landingpage:need-capture:products-list:cta" />
        </Button>
      </m.div>
    </div>
  );
};

export default ProductsList;
